import { AdminLayout } from "@/components/admin-layout";
import { SourceTypeFilter } from "@/components/pay-list-filters";
import { getSourceTypeLabel, payoutRequestSourceTypeOptions } from "@/components/payout-source";
import { ReportExportButton } from "@/components/report-export-button";
import { getErrorMessage } from "@/lib/axios";
import { formatCurrency } from "@/lib/payout-utils";
import { payoutReportExportClient } from "@/lib/report-export-clients";
import { toast } from "@/lib/toast";
import {
	getPayoutReport,
	exportPayoutReport,
	ExportFormat,
	PayoutReportDateBasis,
	PayoutReportDetail,
	PayoutRequestApprovalMethod,
	type PartyReference,
	type PayoutReportDto,
	type PayoutReportPayeeGroupDto,
	type PayoutReportDepositDto,
	type PayoutReportLineDto,
} from "@fp/admin-api-client";
import { ButtonGroup, ButtonGroupItem } from "@fp/ui/components/base/button-group/button-group";
import { formatLocalDate } from "@fp/ui/components/common/local-date-time";
import { AdminPageHeader } from "@/components/admin-page-header";
import {
	PageContentContainer,
	PageLoading,
} from "@fp/ui/components/base/page-layout/page-layout";
import { ComboBox } from "@fp/ui/components/base/select/combobox";
import { SelectItem } from "@fp/ui/components/base/select/select-item";
import { DateRangePicker } from "@fp/ui/components/application/date-picker/date-range-picker";
import {
	getLocalTimeZone,
	startOfMonth,
	endOfMonth,
	today,
	type DateValue,
} from "@internationalized/date";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import type { Key, RangeValue } from "@react-types/shared";

const ALL_PAYEES_KEY = "all";

const now = today(getLocalTimeZone());

const defaultRange: RangeValue<DateValue> = {
	start: startOfMonth(now),
	end: endOfMonth(now),
};

const dateBasisLabels: Record<PayoutReportDateBasis, string> = {
	[PayoutReportDateBasis.Paid]: "Paid date",
	[PayoutReportDateBasis.Completed]: "Completion date",
	[PayoutReportDateBasis.RequestCreated]: "Request date",
};

const detailLabels: Record<PayoutReportDetail, string> = {
	[PayoutReportDetail.Lines]: "Lines",
	[PayoutReportDetail.Deposits]: "Deposits",
	[PayoutReportDetail.Subtotals]: "Subtotals",
};

const approvalMethodLabels: Record<PayoutRequestApprovalMethod, string> = {
	[PayoutRequestApprovalMethod.Administrator]: "by an admin",
	[PayoutRequestApprovalMethod.PreApproved]: "pre-approved",
	[PayoutRequestApprovalMethod.AutoApproved]: "auto-approved",
};

const toIsoDate = (date: DateValue) => {
	const d = date.toDate(getLocalTimeZone());
	return d.toISOString();
};

const payeeKey = (payee: PartyReference) => `${payee.type}:${payee.id}`;

const firstSelected = <T extends string>(keys: Set<Key>, fallback: T) => ([...keys][0] as T | undefined) ?? fallback;

type PayeeOption = { payee: PartyReference; name: string };

type ReportFilters = {
	dateBasis: PayoutReportDateBasis;
	payee: PartyReference | null;
	sourceTypes: Set<string>;
};

const toReportParams = (range: RangeValue<DateValue>, filters: ReportFilters) => ({
	dateBasis: filters.dateBasis,
	startDate: toIsoDate(range.start),
	endDate: toIsoDate(range.end),
	sourceTypes: [...filters.sourceTypes],
	payeeType: filters.payee?.type,
	payeeId: filters.payee?.id,
});

export const Route = createFileRoute("/reporting/payouts")({
	component: RouteComponent,
	pendingComponent: () => <PageLoading />,
});

function RouteComponent() {
	const [dateRange, setDateRange] =
		useState<RangeValue<DateValue>>(defaultRange);
	const handleDateRangeChange = useCallback(
		(value: RangeValue<DateValue> | null) => {
			if (value) setDateRange(value);
		},
		[],
	);
	const [report, setReport] = useState<PayoutReportDto | null>(null);
	const [payeeOptions, setPayeeOptions] = useState<PayeeOption[]>([]);
	const [filters, setFilters] = useState<ReportFilters>({
		dateBasis: PayoutReportDateBasis.Paid,
		payee: null,
		sourceTypes: new Set(),
	});
	const [detail, setDetail] = useState<PayoutReportDetail>(PayoutReportDetail.Lines);
	const [isLoading, setIsLoading] = useState(false);

	const fetchReport = useCallback(
		async (range: RangeValue<DateValue>, nextFilters: ReportFilters) => {
			setIsLoading(true);
			try {
				const response = await getPayoutReport(
					toReportParams(range, nextFilters),
				);
				setReport(response.data);
				if (nextFilters.payee === null) {
					setPayeeOptions(
						response.data.groups.map((g) => ({
							payee: g.payee,
							name: g.name,
						})),
					);
				}
			} catch (err) {
				toast.error(getErrorMessage(err, "Failed to load the report"));
			} finally {
				setIsLoading(false);
			}
		},
		[],
	);

	const applyFilters = useCallback(
		(nextFilters: ReportFilters) => {
			setFilters(nextFilters);
			fetchReport(dateRange, nextFilters);
		},
		[dateRange, fetchReport],
	);

	const handleApply = useCallback(() => {
		setDetail(PayoutReportDetail.Lines);
		applyFilters({ ...filters, payee: null });
	}, [filters, applyFilters]);

	const handleDateBasisChange = useCallback(
		(keys: Set<Key>) => {
			applyFilters({ ...filters, dateBasis: firstSelected(keys, filters.dateBasis), payee: null });
		},
		[filters, applyFilters],
	);

	const handlePayeeChange = useCallback(
		(key: Key | null) => {
			const payee =
				payeeOptions.find((option) => payeeKey(option.payee) === key)
					?.payee ?? null;
			applyFilters({ ...filters, payee });
		},
		[filters, payeeOptions, applyFilters],
	);

	const handleSourceTypesChange = useCallback(
		(sourceTypes: Set<string>) => {
			applyFilters({ ...filters, sourceTypes });
		},
		[filters, applyFilters],
	);

	const exportReport = useCallback(
		(format: ExportFormat) =>
			exportPayoutReport({
				...toReportParams(dateRange, filters),
				detail,
				format,
			}),
		[dateRange, filters, detail],
	);

	const exportKey = JSON.stringify({ ...toReportParams(dateRange, filters), detail });

	return (
		<AdminLayout isWide>
			<AdminPageHeader
				title="Payout Report"
				parent={{ url: "/reporting", label: "Reporting" }}
			/>
			<PageContentContainer className="space-y-6">
				<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
					<div className="flex flex-col gap-4 sm:flex-row sm:items-center">
						<ButtonGroup
							size="sm"
							aria-label="Date the range applies to"
							disallowEmptySelection
							selectedKeys={[filters.dateBasis]}
							onSelectionChange={handleDateBasisChange}
						>
							{Object.values(PayoutReportDateBasis).map((dateBasis) => (
								<ButtonGroupItem key={dateBasis} id={dateBasis}>
									{dateBasisLabels[dateBasis]}
								</ButtonGroupItem>
							))}
						</ButtonGroup>
						<DateRangePicker
							defaultOpen
							value={dateRange}
							onChange={handleDateRangeChange}
							onApply={handleApply}
						/>
					</div>
					{report && (
						<div className="flex gap-2">
							<ReportExportButton key={`csv-${exportKey}`} client={payoutReportExportClient} format={ExportFormat.Csv} label="CSV" request={exportReport} />
							<ReportExportButton key={`pdf-${exportKey}`} client={payoutReportExportClient} format={ExportFormat.Pdf} label="PDF" request={exportReport} />
						</div>
					)}
				</div>

				{report && (
					<SourceTypeFilter
						options={payoutRequestSourceTypeOptions}
						selected={filters.sourceTypes}
						onSelectedChange={handleSourceTypesChange}
					/>
				)}

				{report && (
					<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
						<div className="w-full sm:w-64">
							<ComboBox
								aria-label="Filter by payee"
								placeholder="All payees"
								shortcut={false}
								selectedKey={filters.payee ? payeeKey(filters.payee) : ALL_PAYEES_KEY}
								onSelectionChange={handlePayeeChange}
							>
								<SelectItem id={ALL_PAYEES_KEY}>All payees</SelectItem>
								{payeeOptions.map((option) => (
									<SelectItem key={payeeKey(option.payee)} id={payeeKey(option.payee)}>
										{option.name}
									</SelectItem>
								))}
							</ComboBox>
						</div>
						<ButtonGroup
							size="sm"
							aria-label="Report detail"
							disallowEmptySelection
							selectedKeys={[detail]}
							onSelectionChange={(keys) => setDetail(firstSelected(keys, detail))}
						>
							{Object.values(PayoutReportDetail).map((option) => (
								<ButtonGroupItem key={option} id={option}>
									{detailLabels[option]}
								</ButtonGroupItem>
							))}
						</ButtonGroup>
					</div>
				)}

				{isLoading && (
					<div className="flex items-center justify-center py-12">
						<div className="text-gray-500">Loading report...</div>
					</div>
				)}

				{!isLoading && !report && (
					<div className="text-center py-12 text-gray-500">
						Select a date range and click Apply to generate the report.
					</div>
				)}

				{!isLoading && report && (
					<PayoutReportView
						report={report}
						detail={detail}
						isFiltered={filters.payee !== null || filters.sourceTypes.size > 0}
					/>
				)}
			</PageContentContainer>
		</AdminLayout>
	);
}

function PayoutReportView({
	report,
	detail,
	isFiltered,
}: {
	report: PayoutReportDto;
	detail: PayoutReportDetail;
	isFiltered: boolean;
}) {
	if (report.groups.length === 0) {
		return (
			<div className="text-center py-12 text-gray-500">
				{isFiltered
					? "No payouts match the current filters."
					: "No paid payouts found for the selected period."}
			</div>
		);
	}

	switch (detail) {
		case PayoutReportDetail.Subtotals:
			return <SubtotalsTable report={report} />;
		case PayoutReportDetail.Deposits:
			return <DepositsTable report={report} />;
		case PayoutReportDetail.Lines:
			return (
				<div className="space-y-6">
					{report.groups.map((group) => (
						<PayeeGroup key={payeeKey(group.payee)} group={group} />
					))}

					<div className="border-t-2 border-gray-900 pt-3 flex justify-between items-center">
						<span className="text-base font-bold text-gray-900">Grand Total</span>
						<span className="text-base font-bold text-gray-900">
							{formatCurrency(report.grandTotal, "USD")}
						</span>
					</div>
				</div>
			);
	}
}

function PayeeName({ group }: { group: PayoutReportPayeeGroupDto }) {
	return (
		<span className="flex items-center gap-2">
			{group.name}
			{group.classification && (
				<span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">
					{group.classification}
				</span>
			)}
		</span>
	);
}

function SubtotalsTable({ report }: { report: PayoutReportDto }) {
	return (
		<div className="shrink-0 rounded-lg border border-gray-200 overflow-hidden bg-white">
			<div className="grid grid-cols-[1fr_auto] gap-4 px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
				<span>Name</span>
				<span className="w-32 text-right">Total</span>
			</div>
			{report.groups.map((group, index) => (
				<div
					key={payeeKey(group.payee)}
					className={`grid grid-cols-[1fr_auto] gap-4 px-4 py-2 text-sm ${index % 2 === 1 ? "bg-gray-50/50" : ""}`}
				>
					<span className="text-gray-700">
						<PayeeName group={group} />
					</span>
					<span className="w-32 text-right text-gray-900">
						{formatCurrency(group.subtotal, "USD")}
					</span>
				</div>
			))}
			<div className="grid grid-cols-[1fr_auto] gap-4 px-4 py-3 border-t-2 border-gray-900 font-semibold text-sm">
				<span className="text-gray-900">Grand Total</span>
				<span className="w-32 text-right text-gray-900">
					{formatCurrency(report.grandTotal, "USD")}
				</span>
			</div>
		</div>
	);
}

const depositsGrid = "grid grid-cols-[1fr_8rem_minmax(0,2fr)_8rem_minmax(0,1fr)] gap-4 px-4";

function DepositsTable({ report }: { report: PayoutReportDto }) {
	const depositsTotal = report.groups.reduce((total, group) => total + group.deposits.reduce((sum, deposit) => sum + deposit.amount, 0), 0);

	return (
		<div className="shrink-0 rounded-lg border border-gray-200 overflow-hidden bg-white">
			<div className={`${depositsGrid} py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50`}>
				<span>Name</span>
				<span>Deposit Date</span>
				<span>Description</span>
				<span className="text-right">Deposit Total</span>
				<span>Reference</span>
			</div>
			{report.groups.flatMap((group) =>
				group.deposits.map((deposit) => (
					<div key={deposit.reference} className={`${depositsGrid} py-2 text-sm border-t border-gray-100`}>
						<span className="text-gray-700">
							<PayeeName group={group} />
						</span>
						<span className="text-gray-500">{formatLocalDate(deposit.settledDate, "dateWithYear")}</span>
						<span className="text-gray-700">{deposit.description}</span>
						<span className="text-right text-gray-900">{formatCurrency(deposit.amount, "USD")}</span>
						<span className="text-gray-500 font-mono text-xs truncate" title={deposit.reference}>{deposit.reference}</span>
					</div>
				)),
			)}
			<div className={`${depositsGrid} py-3 border-t-2 border-gray-900 font-semibold text-sm`}>
				<span className="col-span-3 text-gray-900">Grand Total</span>
				<span className="text-right text-gray-900">{formatCurrency(depositsTotal, "USD")}</span>
			</div>
		</div>
	);
}

function PayeeGroup({ group }: { group: PayoutReportPayeeGroupDto }) {
	return (
		<div className="rounded-lg border border-gray-200 overflow-hidden bg-white">
			<div className="px-4 py-3 font-semibold text-gray-900">
				<PayeeName group={group} />
			</div>

			{group.deposits.map((deposit) => (
				<Deposit key={deposit.reference} deposit={deposit} />
			))}

			<div className="flex justify-between px-4 py-3 border-t border-gray-200 font-semibold text-sm">
				<span className="text-gray-700">Subtotal</span>
				<span className="text-gray-900">{formatCurrency(group.subtotal, "USD")}</span>
			</div>
		</div>
	);
}

function Deposit({ deposit }: { deposit: PayoutReportDepositDto }) {
	return (
		<div className="border-t border-gray-200">
			<div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 bg-gray-50 text-sm">
				<span className="text-gray-700">
					Deposit {formatLocalDate(deposit.settledDate, "dateWithYear")}
					<span className="ml-2 font-mono text-xs text-gray-500">{deposit.reference}</span>
				</span>
				<span className="font-semibold text-gray-900">{formatCurrency(deposit.amount, "USD")}</span>
			</div>

			<div className="overflow-x-auto">
				<table className="w-full text-sm">
					<thead>
						<tr className="text-xs font-semibold text-gray-500 uppercase tracking-wider text-left">
							<th className="px-4 py-2">Invoice</th>
							<th className="px-4 py-2">Source</th>
							<th className="px-4 py-2">Job / Description</th>
							<th className="px-4 py-2">Customer</th>
							<th className="px-4 py-2">Address</th>
							<th className="px-4 py-2">Job Ref</th>
							<th className="px-4 py-2">Completed</th>
							<th className="px-4 py-2">Requested</th>
							<th className="px-4 py-2">Approved</th>
							<th className="px-4 py-2 text-right">Gross</th>
							<th className="px-4 py-2 text-right">Fee</th>
							<th className="px-4 py-2 text-right">Net</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-gray-100">
						{deposit.lines.map((line, index) => (
							<PayoutReportRow key={`${deposit.reference}-${index}`} line={line} />
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
}

function PayoutReportRow({ line }: { line: PayoutReportLineDto }) {
	return (
		<tr className="align-top">
			<td className="px-4 py-2 text-gray-500 whitespace-nowrap">{line.invoiceNumber}</td>
			<td className="px-4 py-2 text-gray-500 whitespace-nowrap">{getSourceTypeLabel(line.sourceType)}</td>
			<td className="px-4 py-2 text-gray-700">{line.jobType ?? line.description}</td>
			<td className="px-4 py-2 text-gray-700">{line.customer}</td>
			<td className="px-4 py-2 text-gray-500">{line.address}</td>
			<td className="px-4 py-2 text-gray-500 whitespace-nowrap">{line.jobReference}</td>
			<td className="px-4 py-2 text-gray-500 whitespace-nowrap">
				{line.completedDate && formatLocalDate(line.completedDate, "dateWithYear")}
			</td>
			<td className="px-4 py-2 text-gray-500">
				<div className="whitespace-nowrap">{formatLocalDate(line.requestCreatedDate, "dateWithYear")}</div>
				{line.requestedBy && <div className="text-xs">{line.requestedBy}</div>}
			</td>
			<td className="px-4 py-2 text-gray-500">
				{line.approvedDate && <div className="whitespace-nowrap">{formatLocalDate(line.approvedDate, "dateWithYear")}</div>}
				{(line.approvedBy || line.approvalMethod) && (
					<div className="text-xs">
						{[line.approvedBy, line.approvalMethod && approvalMethodLabels[line.approvalMethod]].filter(Boolean).join(", ")}
					</div>
				)}
			</td>
			<td className="px-4 py-2 text-right text-gray-900 whitespace-nowrap">{formatCurrency(line.gross, "USD")}</td>
			<td className="px-4 py-2 text-right text-gray-500 whitespace-nowrap">{formatCurrency(line.fee, "USD")}</td>
			<td className="px-4 py-2 text-right text-gray-900 whitespace-nowrap">{formatCurrency(line.net, "USD")}</td>
		</tr>
	);
}
